import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

interface ProgressItem {
  report: {
    id?: number;
    report_uid: string;
    status?: string;
    created_at?: string;
    incident_type?: string;
    urgency?: string;
    description?: string;
    location?: string;
    address?: string;
    barangay?: string;
    municipality?: string;
    province?: string;
    reporter_name?: string;
    reporter_contact?: string;
  };

  case: {
    case_number?: string;
    case_status?: string;
    progress_note?: string;
    priority_level?: string;
    report_date?: string;
    report_source?: string;
    referral_source?: string;
    incident_date?: string;
    incident_location?: string;
    created_at?: string;
  };

  child: {
    name?: string;
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    nickname?: string;
    birth_date?: string;
    age?: number;
    gender?: string;
    school?: string;
    grade?: string;
    profile_img?: string;
  };
}

interface StatusType {
  name: string;
  code: string;
  description?: string;
  color?: string;
  active: boolean;
}

interface SystemConfig {
  config_key: string;
  config_value: unknown;
}

/* =========================================================
   STATUS HELPERS
========================================================= */

function normalizeStatus(value?: string) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function statusKey(value?: string) {
  return normalizeStatus(value).replace(/\s+/g, '_');
}

function parseStatusTypes(value: unknown): StatusType[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item: any): StatusType | null => {
      /*
       * Supports old configuration format:
       *
       * [
       *   "Open",
       *   "Ongoing",
       *   "Closed"
       * ]
       */
      if (typeof item === 'string') {
        const name = item.trim();

        if (!name) {
          return null;
        }

        return {
          name,
          code: statusKey(name),
          description: '',
          color: '',
          active: true,
        };
      }

      /*
       * Supports current configuration format:
       *
       * {
       *   name: "Open",
       *   code: "OPEN",
       *   description: "...",
       *   color: "...",
       *   active: true
       * }
       */
      if (
        item &&
        typeof item === 'object'
      ) {
        const name = String(
          item.name || ''
        ).trim();

        if (!name) {
          return null;
        }

        const code = String(
          item.code ||
            statusKey(name)
        ).trim();

        return {
          name,
          code,
          description: String(
            item.description || ''
          ),
          color: String(
            item.color || ''
          ),
          active:
            item.active === undefined
              ? true
              : Boolean(item.active),
        };
      }

      return null;
    })
    .filter(
      (
        item
      ): item is StatusType =>
        item !== null &&
        item.active !== false
    );
}

/*
 * Finds the configured status using:
 *
 * Name:
 *   Open
 *
 * Code:
 *   OPEN
 *
 * Also handles:
 *   for_approval
 *   FOR_APPROVAL
 *   For Approval
 *   for approval
 */
function getStatusConfig(
  status: string | undefined,
  statusTypes: StatusType[]
) {
  const normalized =
    normalizeStatus(status);

  const key =
    statusKey(status);

  if (
    !normalized &&
    !key
  ) {
    return undefined;
  }

  return statusTypes.find(
    (item) => {
      const itemName =
        normalizeStatus(item.name);

      const itemCode =
        normalizeStatus(item.code);

      const itemNameKey =
        statusKey(item.name);

      const itemCodeKey =
        statusKey(item.code);

      return (
        itemName === normalized ||
        itemCode === normalized ||
        itemNameKey === key ||
        itemCodeKey === key
      );
    }
  );
}

/* =========================================================
   STATUS COLORS
========================================================= */

function getStatusStyle(
  status: string | undefined,
  statusTypes: StatusType[]
) {
  const config =
    getStatusConfig(
      status,
      statusTypes
    );

  /*
   * If Admin stored Tailwind classes,
   * use them directly.
   */
  if (config?.color) {
    const color =
      config.color.trim();

    if (
      color.includes('bg-') ||
      color.includes('text-') ||
      color.includes('border-')
    ) {
      return color;
    }

    const normalizedColor =
      color.toLowerCase();

    if (
      normalizedColor.includes(
        'green'
      )
    ) {
      return 'bg-green-100 text-green-700 border border-green-200';
    }

    if (
      normalizedColor.includes(
        'blue'
      )
    ) {
      return 'bg-blue-100 text-blue-700 border border-blue-200';
    }

    if (
      normalizedColor.includes(
        'yellow'
      )
    ) {
      return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    }

    if (
      normalizedColor.includes(
        'orange'
      )
    ) {
      return 'bg-orange-100 text-orange-700 border border-orange-200';
    }

    if (
      normalizedColor.includes(
        'red'
      )
    ) {
      return 'bg-red-100 text-red-700 border border-red-200';
    }

    if (
      normalizedColor.includes(
        'purple'
      )
    ) {
      return 'bg-purple-100 text-purple-700 border border-purple-200';
    }

    if (
      normalizedColor.includes(
        'gray'
      ) ||
      normalizedColor.includes(
        'grey'
      )
    ) {
      return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  }

  /*
   * Fallback styling.
   * This is only used when Admin has not
   * configured a color.
   */
  const normalized =
    normalizeStatus(status);

  if (
    normalized.includes(
      'resolved'
    ) ||
    normalized.includes(
      'closed'
    ) ||
    normalized.includes(
      'completed'
    )
  ) {
    return 'bg-green-100 text-green-700 border border-green-200';
  }

  if (
    normalized.includes(
      'ongoing'
    ) ||
    normalized.includes(
      'progress'
    ) ||
    normalized.includes(
      'review'
    ) ||
    normalized.includes(
      'processing'
    )
  ) {
    return 'bg-blue-100 text-blue-700 border border-blue-200';
  }

  if (
    normalized.includes(
      'open'
    ) ||
    normalized.includes(
      'pending'
    )
  ) {
    return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
  }

  if (
    normalized.includes(
      'urgent'
    ) ||
    normalized.includes(
      'critical'
    )
  ) {
    return 'bg-red-100 text-red-700 border border-red-200';
  }

  if (
    normalized.includes(
      'escalated'
    )
  ) {
    return 'bg-orange-100 text-orange-700 border border-orange-200';
  }

  return 'bg-gray-100 text-gray-700 border border-gray-200';
}

/* =========================================================
   STATUS ICONS
========================================================= */

function getStatusIcon(
  status: string | undefined
) {
  const normalized =
    normalizeStatus(status);

  if (
    normalized.includes(
      'resolved'
    ) ||
    normalized.includes(
      'closed'
    ) ||
    normalized.includes(
      'completed'
    )
  ) {
    return 'ri-checkbox-circle-line';
  }

  if (
    normalized.includes(
      'review'
    ) ||
    normalized.includes(
      'approval'
    )
  ) {
    return 'ri-search-eye-line';
  }

  if (
    normalized.includes(
      'ongoing'
    ) ||
    normalized.includes(
      'progress'
    ) ||
    normalized.includes(
      'processing'
    )
  ) {
    return 'ri-loader-4-line';
  }

  if (
    normalized.includes(
      'urgent'
    ) ||
    normalized.includes(
      'critical'
    )
  ) {
    return 'ri-error-warning-line';
  }

  if (
    normalized.includes(
      'open'
    ) ||
    normalized.includes(
      'pending'
    )
  ) {
    return 'ri-time-line';
  }

  if (
    normalized.includes(
      'referral'
    )
  ) {
    return 'ri-share-forward-line';
  }

  if (
    normalized.includes(
      'escalated'
    )
  ) {
    return 'ri-arrow-up-circle-line';
  }

  return 'ri-checkbox-blank-circle-line';
}

/* =========================================================
   TIMELINE DESCRIPTION
========================================================= */

function getTimelineDescription(
  status: StatusType,
  isCurrent: boolean,
  isCompleted: boolean
) {
  if (isCurrent) {
    return (
      status.description ||
      'Current case status'
    );
  }

  if (isCompleted) {
    return (
      status.description ||
      'Completed'
    );
  }

  return (
    status.description ||
    'Pending'
  );
}

/* =========================================================
   MAIN PAGE
========================================================= */

export default function VProgressPage() {
  const { user } =
    useAuth();

  const [
    cases,
    setCases,
  ] = useState<
    ProgressItem[]
  >([]);

  const [
    statusTypes,
    setStatusTypes,
  ] = useState<
    StatusType[]
  >([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadingStatusTypes,
    setLoadingStatusTypes,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState('');

  const victim_uid =
    user?.victim_uid ||
    user?.uid ||
    '';

  /* =======================================================
     LOAD STATUS TYPES
  ======================================================= */

  useEffect(() => {
    let cancelled = false;

    const fetchStatusTypes =
      async () => {
        try {
          setLoadingStatusTypes(
            true
          );

          const response =
            await fetch(
              'https://guardiancloudcare.com/backend/api/get_system_config.php',
              {
                method: 'GET',
                headers: {
                  Accept:
                    'application/json',
                },
              }
            );

          const text =
            await response.text();

          if (!text.trim()) {
            throw new Error(
              'The system configuration server returned an empty response.'
            );
          }

          let result: any;

          try {
            result =
              JSON.parse(text);
          } catch {
            throw new Error(
              'The system configuration API returned invalid JSON.'
            );
          }

          if (!response.ok) {
            throw new Error(
              result?.error ||
                `System configuration error: HTTP ${response.status}`
            );
          }

          if (
            result?.success ===
            false
          ) {
            throw new Error(
              result?.error ||
                'Unable to load status configuration.'
            );
          }

          const configs:
            SystemConfig[] =
            Array.isArray(
              result?.data
            )
              ? result.data
              : [];

          const statusConfig =
            configs.find(
              (config) =>
                config.config_key ===
                'status_types'
            );

          const activeStatuses =
            parseStatusTypes(
              statusConfig?.config_value
            );

          console.log(
            'ACTIVE VICTIM STATUS TYPES:',
            activeStatuses
          );

          if (!cancelled) {
            setStatusTypes(
              activeStatuses
            );
          }
        } catch (err) {
          console.error(
            'Failed to load status types:',
            err
          );

          if (!cancelled) {
            setStatusTypes([]);
          }
        } finally {
          if (!cancelled) {
            setLoadingStatusTypes(
              false
            );
          }
        }
      };

    fetchStatusTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  /* =======================================================
     LOAD VICTIM PROGRESS
  ======================================================= */

  useEffect(() => {
    let cancelled = false;

    const fetchProgress =
      async () => {
        if (!victim_uid) {
          console.warn(
            'No victim UID found in logged-in user.'
          );

          setCases([]);

          setError(
            'Unable to identify your account. Please log in again.'
          );

          setLoading(false);

          return;
        }

        try {
          setLoading(true);
          setError('');

          const url =
            `https://guardiancloudcare.com/backend/api/victim/get_progress.php?victim_uid=${encodeURIComponent(
              victim_uid
            )}`;

          console.log(
            'Fetching victim progress:',
            url
          );

          const response =
            await fetch(
              url,
              {
                method: 'GET',
                headers: {
                  Accept:
                    'application/json',
                },
              }
            );

          const text =
            await response.text();

          console.log(
            'Progress HTTP Status:',
            response.status
          );

          console.log(
            'Progress Raw Response:',
            text
          );

          if (!text.trim()) {
            throw new Error(
              `The progress server returned an empty response. HTTP ${response.status}`
            );
          }

          let result: any;

          try {
            result =
              JSON.parse(text);
          } catch (
            jsonError
          ) {
            console.error(
              'Invalid JSON from get_progress.php:',
              text
            );

            throw new Error(
              'The progress API returned an invalid response.'
            );
          }

          console.log(
            'VICTIM PROGRESS RESPONSE:',
            result
          );

          if (!response.ok) {
            throw new Error(
              result?.error ||
                `Server error: HTTP ${response.status}`
            );
          }

          if (
            result?.success ===
            false
          ) {
            throw new Error(
              result?.error ||
                'Unable to load your case progress.'
            );
          }

          let progressData:
            any[] = [];

          if (
            Array.isArray(
              result
            )
          ) {
            progressData =
              result;
          } else if (
            Array.isArray(
              result?.data
            )
          ) {
            progressData =
              result.data;
          } else {
            console.warn(
              'Unexpected progress response:',
              result
            );

            progressData = [];
          }

          console.log(
            'PROGRESS DATA:',
            progressData
          );

          if (!cancelled) {
            setCases(
              progressData
            );
          }
        } catch (err) {
          console.error(
            'Check Progress Error:',
            err
          );

          if (!cancelled) {
            setCases([]);

            setError(
              err instanceof Error
                ? err.message
                : 'Unable to load your case progress.'
            );
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      };

    fetchProgress();

    return () => {
      cancelled = true;
    };
  }, [victim_uid]);

  /* =======================================================
     MATCH CASES WITH CONFIGURED STATUS
  ======================================================= */

  const configuredCases =
    useMemo(() => {
      return cases.map(
        (item) => {
          const rawStatus =
            item.case
              .case_status ||
            item.report
              .status ||
            '';

          const config =
            getStatusConfig(
              rawStatus,
              statusTypes
            );

          return {
            ...item,
            rawStatus,
            statusConfig:
              config,
          };
        }
      );
    }, [
      cases,
      statusTypes,
    ]);

  /* =======================================================
     DATE FORMAT
  ======================================================= */

  const formatDate = (
    date?: string
  ) => {
    if (!date) {
      return 'Not available';
    }

    const parsed =
      new Date(date);

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return date;
    }

    return parsed.toLocaleDateString(
      'en-US',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    );
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <AppLayout
      title="Check Progress"
      breadcrumb={[
        'Victim',
        'Check Progress',
      ]}
    >
      <div className="max-w-6xl mx-auto space-y-6">

        {/* HEADER */}
        <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
          <div className="flex items-start gap-4">

            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-progress-3-line text-blue-500 text-2xl" />
            </div>

            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                Check Case Progress
              </h1>

              <p className="text-sm text-gray-500 mt-2">
                View the current status and progress of
                your submitted reports.
              </p>
            </div>

          </div>
        </section>

        {/* LOADING */}
        {loading && (
          <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">

            <i className="ri-loader-4-line animate-spin text-3xl text-blue-500" />

            <p className="text-sm text-gray-500 mt-3">
              Loading your reports...
            </p>

          </section>
        )}

        {/* ERROR */}
        {!loading &&
          error && (
            <section className="bg-red-50 border border-red-200 rounded-2xl p-5">

              <div className="flex items-start gap-3">

                <i className="ri-error-warning-line text-red-500 text-xl" />

                <div>
                  <h3 className="font-bold text-red-800">
                    Unable to load progress
                  </h3>

                  <p className="text-sm text-red-700 mt-1">
                    {error}
                  </p>
                </div>

              </div>

            </section>
          )}

        {/* NO REPORTS */}
        {!loading &&
          !error &&
          cases.length === 0 && (
            <section className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center">

              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <i className="ri-file-search-line text-gray-400 text-2xl" />
              </div>

              <h2 className="text-lg font-bold text-gray-800 mt-4">
                No Reports Found
              </h2>

              <p className="text-sm text-gray-500 mt-2">
                You have not submitted any reports yet.
              </p>

            </section>
          )}

        {/* REPORTS */}
        {!loading &&
          !error &&
          cases.length > 0 && (
            <div className="space-y-6">

              {configuredCases.map(
                (item) => (
                  <ProgressCard
                    key={
                      item.report
                        .report_uid
                    }
                    item={item}
                    statusTypes={
                      statusTypes
                    }
                    loadingStatusTypes={
                      loadingStatusTypes
                    }
                    formatDate={
                      formatDate
                    }
                  />
                )
              )}

            </div>
          )}

      </div>
    </AppLayout>
  );
}

/* =========================================================
   PROGRESS CARD
========================================================= */

function ProgressCard({
  item,
  statusTypes,
  loadingStatusTypes,
  formatDate,
}: {
  item: ProgressItem & {
    rawStatus: string;
    statusConfig?: StatusType;
  };

  statusTypes: StatusType[];

  loadingStatusTypes: boolean;

  formatDate: (
    date?: string
  ) => string;
}) {
  const hasCase =
    !!item.case
      .case_number;

  const configuredStatus =
    item.statusConfig;

  const rawStatus =
    item.rawStatus;

  /*
   * If the current database status is
   * still active/configured, display
   * the Admin status name.
   *
   * Example:
   *
   * DB: FOR_APPROVAL
   * Admin: For Approval
   *
   * Display:
   * For Approval
   */
  const displayStatus =
    configuredStatus?.name ||
    (
      statusTypes.length > 0
        ? 'Status unavailable'
        : rawStatus || 'Pending'
    );

  const statusStyle =
    configuredStatus
      ? getStatusStyle(
          configuredStatus.name,
          statusTypes
        )
      : statusTypes.length > 0
        ? 'bg-gray-100 text-gray-600 border border-gray-200'
        : getStatusStyle(
            rawStatus,
            statusTypes
          );

  return (
    <section className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">

      {/* HEADER */}
      <div className="p-6 border-b border-gray-100">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

          <div>

            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
              Report Reference
            </p>

            <h2 className="text-lg font-extrabold text-gray-900 mt-1">
              {item.report.report_uid}
            </h2>

            {hasCase && (
              <p className="text-sm text-gray-500 mt-1">
                Case Number:{' '}

                <span className="font-bold text-gray-700">
                  {item.case.case_number}
                </span>
              </p>
            )}

          </div>

          <span
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold w-fit ${statusStyle}`}
          >
            <span className="w-2 h-2 rounded-full bg-current" />

            {loadingStatusTypes
              ? 'Loading status...'
              : displayStatus}
          </span>

        </div>

      </div>

      <div className="p-6">

        {/* CHILD */}
        <div className="bg-gray-50 rounded-2xl p-5">

          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <i className="ri-user-heart-line text-blue-600 text-xl" />
            </div>

            <div>

              <p className="text-xs text-gray-400 font-semibold uppercase">
                Child
              </p>

              <h3 className="font-bold text-gray-900">
                {item.child.name ||
                  'Not available'}
              </h3>

              <p className="text-sm text-gray-500">

                {item.child.age
                  ? `${item.child.age} years old`
                  : ''}

                {item.child.gender
                  ? ` • ${item.child.gender}`
                  : ''}

              </p>

            </div>

          </div>

        </div>

        {/* DETAILS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">

          <InfoBox
            icon="ri-alert-line"
            label="Incident"
            value={
              item.report
                .incident_type ||
              'Not available'
            }
          />

          <InfoBox
            icon="ri-alarm-warning-line"
            label="Urgency"
            value={
              item.report
                .urgency ||
              'Not available'
            }
          />

          <InfoBox
            icon="ri-calendar-line"
            label="Submitted"
            value={formatDate(
              item.report
                .created_at
            )}
          />

        </div>

        {/* CURRENT PROGRESS */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl p-5">

          <div className="flex items-start gap-3">

            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-progress-3-line text-blue-600" />
            </div>

            <div>

              <h3 className="font-bold text-blue-900">
                Current Progress
              </h3>

              <p className="text-sm text-blue-800 mt-2 leading-relaxed">
                {item.case
                  .progress_note ||
                  'Your report has been received. Please check back for updates.'}
              </p>

            </div>

          </div>

        </div>

        {/* CASE PROGRESS */}
        <div className="mt-7">

          <h3 className="font-bold text-gray-800 mb-5">
            Case Progress
          </h3>

          <ProgressTimeline
            status={rawStatus}
            statusTypes={
              statusTypes
            }
            loading={
              loadingStatusTypes
            }
          />

        </div>

        {/* LOCATION */}
        {(item.report.location ||
          item.report.barangay ||
          item.report.municipality ||
          item.report.province) && (

          <div className="mt-6">

            <InfoBox
              icon="ri-map-pin-line"
              label="Location"
              value={[
                item.report.location,
                item.report.barangay,
                item.report.municipality,
                item.report.province,
              ]
                .filter(Boolean)
                .join(', ')}
            />

          </div>

        )}

      </div>

    </section>
  );
}

/* =========================================================
   PROGRESS TIMELINE
========================================================= */

function ProgressTimeline({
  status,
  statusTypes,
  loading,
}: {
  status: string;
  statusTypes: StatusType[];
  loading: boolean;
}) {
  const normalizedStatus =
    normalizeStatus(status);

  const normalizedStatusKey =
    statusKey(status);

  /*
   * Find the current status using BOTH:
   *
   * name
   * code
   *
   * Example:
   *
   * Current:
   * FOR_APPROVAL
   *
   * Config:
   * For Approval
   * FOR_APPROVAL
   *
   * Result:
   * index = 2
   */
  const activeIndex =
    useMemo(() => {
      if (
        !normalizedStatus &&
        !normalizedStatusKey
      ) {
        return -1;
      }

      if (
        statusTypes.length === 0
      ) {
        return -1;
      }

      return statusTypes.findIndex(
        (step) => {
          const name =
            normalizeStatus(
              step.name
            );

          const code =
            normalizeStatus(
              step.code
            );

          const nameKey =
            statusKey(
              step.name
            );

          const codeKey =
            statusKey(
              step.code
            );

          return (
            name ===
              normalizedStatus ||
            code ===
              normalizedStatus ||
            nameKey ===
              normalizedStatusKey ||
            codeKey ===
              normalizedStatusKey
          );
        }
      );
    }, [
      normalizedStatus,
      normalizedStatusKey,
      statusTypes,
    ]);

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">

        <i className="ri-loader-4-line animate-spin text-xl text-teal-500" />

        <p className="text-sm text-gray-500 mt-2">
          Loading case progress...
        </p>

      </div>
    );
  }

  /* =======================================================
     NO ACTIVE STATUS CONFIGURATION
  ======================================================= */

  if (
    statusTypes.length === 0
  ) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center">

        <i className="ri-error-warning-line text-xl text-gray-400" />

        <p className="text-sm text-gray-500 mt-2">
          No active case status configuration available.
        </p>

      </div>
    );
  }

  /* =======================================================
     TIMELINE
  ======================================================= */

  return (
    <div
      className="
        grid
        grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-3
        xl:grid-cols-4
        gap-4
      "
    >

      {statusTypes.map(
        (
          step,
          index
        ) => {

          const isCurrent =
            index ===
            activeIndex;

          const isCompleted =
            activeIndex >= 0 &&
            index <
              activeIndex;

          return (
            <div
              key={`${step.code}-${index}`}
              className={`
                rounded-2xl
                border
                p-5
                text-center
                transition-all
                ${
                  isCurrent
                    ? 'bg-teal-500 border-teal-500 text-white shadow-md scale-[1.01]'
                    : isCompleted
                      ? 'bg-teal-50 border-teal-200 text-teal-800'
                      : 'bg-white border-gray-200 text-gray-600'
                }
              `}
            >

              {/* ICON */}
              <div className="flex items-center justify-center mb-3">

                <div
                  className={`
                    w-11
                    h-11
                    rounded-full
                    flex
                    items-center
                    justify-center
                    text-lg
                    ${
                      isCurrent
                        ? 'bg-white/20 text-white'
                        : isCompleted
                          ? 'bg-teal-100 text-teal-600'
                          : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >

                  {isCurrent ? (

                    <i
                      className={getStatusIcon(
                        step.name
                      )}
                    />

                  ) : isCompleted ? (

                    <i className="ri-check-line" />

                  ) : (

                    <i
                      className={getStatusIcon(
                        step.name
                      )}
                    />

                  )}

                </div>

              </div>

              {/* STATUS NAME */}
              <h3
                className={`
                  font-bold
                  text-sm
                  md:text-base
                  ${
                    isCurrent
                      ? 'text-white'
                      : isCompleted
                        ? 'text-teal-800'
                        : 'text-gray-700'
                  }
                `}
              >
                {step.name}
              </h3>

              {/* DESCRIPTION */}
              <p
                className={`
                  text-xs
                  mt-2
                  leading-relaxed
                  ${
                    isCurrent
                      ? 'text-white/80'
                      : isCompleted
                        ? 'text-teal-600'
                        : 'text-gray-400'
                  }
                `}
              >
                {getTimelineDescription(
                  step,
                  isCurrent,
                  isCompleted
                )}
              </p>

              {/* STATUS LABEL */}
              <div
                className={`
                  mt-3
                  inline-flex
                  items-center
                  gap-1.5
                  text-[11px]
                  font-semibold
                  px-2.5
                  py-1
                  rounded-full
                  ${
                    isCurrent
                      ? 'bg-white/20 text-white'
                      : isCompleted
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-gray-100 text-gray-400'
                  }
                `}
              >

                <span className="w-1.5 h-1.5 rounded-full bg-current" />

                {isCurrent
                  ? 'Current'
                  : isCompleted
                    ? 'Completed'
                    : 'Pending'}

              </div>

            </div>
          );
        }
      )}

    </div>
  );
}

/* =========================================================
   INFO BOX
========================================================= */

function InfoBox({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-gray-50 rounded-2xl p-4">

      <div className="flex items-center gap-2 text-gray-400">

        <i
          className={`${icon} text-lg`}
        />

        <span className="text-xs font-semibold uppercase">
          {label}
        </span>

      </div>

      <p className="font-bold text-gray-800 mt-2 break-words">
        {value}
      </p>

    </div>
  );
}